import { useNavigate } from 'react-router-dom';
import { Brain, CalendarCheck, ChevronRight } from 'lucide-react';
import { Avatar } from '@/components/ui';
import { cn } from '@/lib/utils';
import { encodeUrl } from '@/utils';
import { getUserData } from '@/utils/cookies';
import { userHomeCardClassName } from './userHomeCardStyles';

type UserHomeContinueWithProfessionalCardProps = {
	/** Última sessão de psicólogo já realizada pelo usuário. */
	session: Sessions.Session;
	/** Serviço de psicologia (rótulo e link "outro psicólogo"). */
	service: Services.Service | null;
	className?: string;
};

const formatLastSessionDate = (value: string) => {
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return null;
	return date.toLocaleDateString('pt-BR', {
		day: '2-digit',
		month: 'short',
		year: 'numeric',
	});
};

/**
 * Card "Tudo pronto para continuar com X?": sugere ao usuário agendar de novo
 * com o último psicólogo com quem ele fez sessão. Só aparece para quem já
 * concluiu ao menos uma sessão de psicologia e não tem consulta futura.
 */
const UserHomeContinueWithProfessionalCard = ({
	session,
	service,
	className,
}: UserHomeContinueWithProfessionalCardProps) => {
	const navigate = useNavigate();
	const professional = session.professional;
	const professionalId = professional?.id ?? session.professional_id ?? '';

	if (!professionalId) return null;

	const userFirstName = getUserData()?.first_name?.trim() || 'você';
	const professionalFirstName =
		professional?.first_name?.trim().split(/\s+/)[0] || 'seu psicólogo';
	// Nome completo só no corpo do card; título e botão usam o primeiro nome.
	const professionalFullName = professional
		? [
				professional.name_prefix,
				professional.first_name,
				professional.last_name,
		  ]
				.filter(Boolean)
				.join(' ')
				.trim() || professionalFirstName
		: professionalFirstName;
	const serviceLabel =
		service?.professional_label?.trim() ||
		service?.service_name ||
		'Psicólogo(a)';
	const lastSessionDate = formatLastSessionDate(session.selected_date);
	const serviceId = service?.id ?? session.service_id;

	const handleScheduleAgain = () => {
		const params = new URLSearchParams();
		if (serviceId) params.set('service_id', serviceId);
		params.set('d', encodeUrl({ s: 2, p: professionalId }));
		navigate(`/consulta?${params.toString()}`);
	};

	const handleTryAnother = () => {
		const params = new URLSearchParams();
		if (serviceId) params.set('service_id', serviceId);
		else params.set('service', 'psychologist');
		navigate(`/consulta?${params.toString()}`);
	};

	return (
		<section
			className={cn('w-full space-y-4', className)}
			data-testid="user-home-continue-with-professional"
		>
			<div className="space-y-1">
				<p className="text-sm font-medium text-zinc-500">
					Tudo bem, {userFirstName}?
				</p>
				<h2 className="text-2xl font-bold tracking-tight text-zinc-900 md:text-3xl">
					Tudo pronto para continuar com {professionalFirstName}?
				</h2>
			</div>

			<div
				className={cn(
					userHomeCardClassName,
					'mx-auto w-full max-w-xl text-center',
				)}
			>
				<div
					className="pointer-events-none absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-brand-orange/15 to-transparent"
					aria-hidden
				/>

				<div className="relative z-10 flex flex-col items-center gap-5">
					<Avatar
						img_url={professional?.profile_photo_url || ''}
						variant="rounded"
						className="size-24 rounded-3xl shadow-md ring-4 ring-white [&>svg]:size-12"
					/>

					<div className="space-y-2">
						<h3 className="text-xl font-bold leading-tight text-zinc-900 md:text-2xl">
							{professionalFullName}
						</h3>
						<div className="flex flex-wrap items-center justify-center gap-2">
							<span className="inline-flex items-center gap-1.5 rounded-full bg-brand-orange/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-brand-orange">
								<Brain className="size-3.5" aria-hidden />
								{serviceLabel}
							</span>
							{professional?.crm_or_crp ? (
								<span className="text-xs font-medium text-zinc-500">
									CRP {professional.crm_or_crp}
								</span>
							) : null}
						</div>
					</div>

					<dl className="flex items-stretch">
						{lastSessionDate ? (
							<div className="px-5">
								<dt className="flex items-center justify-center gap-1 text-base font-bold text-zinc-900">
									<CalendarCheck
										className="size-4 text-zinc-500"
										aria-hidden
									/>
									{lastSessionDate}
								</dt>
								<dd className="text-xs text-zinc-500">
									última sessão
								</dd>
							</div>
						) : null}
					</dl>

					<div className="flex w-full flex-col items-center gap-3 pt-1">
						<button
							type="button"
							onClick={handleScheduleAgain}
							className="group inline-flex w-full max-w-xs items-center justify-center gap-2 rounded-full bg-brand-orange px-8 py-3 text-base font-bold text-white shadow-sm transition-all hover:bg-brand-orange/90"
						>
							Agendar novamente
							<ChevronRight
								size={18}
								className="transition-transform group-hover:translate-x-0.5"
								aria-hidden
							/>
						</button>
						<button
							type="button"
							onClick={handleTryAnother}
							className="text-sm font-semibold text-zinc-700 underline underline-offset-4 transition-colors hover:text-zinc-900"
						>
							Experimentar outro psicólogo
						</button>
					</div>
				</div>
			</div>
		</section>
	);
};

export { UserHomeContinueWithProfessionalCard };
